import updateConversationsCategoriesInternal from './helpers/updateConversationsCategoriesInternal';
import updateItemsCategoriesInternal from './helpers/updateItemsCategoriesInternal';
import datapoints from '../datapoints';
import { TableView } from 'owa-mail-list-store';
import { ActionSource } from 'owa-mail-store';
import ReactListViewType from 'owa-service/lib/contract/ReactListViewType';
import { createLegacyActionWithDatapoint } from 'owa-analytics-actions';
import { userMailInteractionAction } from 'owa-mail-actions';
import getInstrumentationContextsFromTableView from 'owa-mail-list-store/lib/utils/getInstrumentationContextsFromTableView';

/**
 * Add categories to the specified rows from the table
 * @param rowKeys of the rows to add categories to
 * @param tableView that contains the rows
 * @param categoriesToAdd the categories to be added to the rows
 * @param actionSource the action source
 * @param createNewCategoryPromise the promise for creating new category request
 */
export const addCategoriesFromTable = createLegacyActionWithDatapoint(
    'addCategories',
    datapoints.addCategories
)(function addCategoriesFromTable(
    rowKeys: string[],
    tableView: TableView,
    categoriesToAdd: string[],
    actionSource: ActionSource,
    createNewCategoryPromise?: Promise<boolean>
) {
    const updateCategoriesPromise = updateRowCategoriesInternal(
        rowKeys,
        tableView,
        categoriesToAdd,
        null /* categoriesToRemove */,
        false /* clearCategories */,
        actionSource,
        createNewCategoryPromise
    );

    const instrumentationContexts = getInstrumentationContextsFromTableView(rowKeys, tableView);
    userMailInteractionAction('Categorize', instrumentationContexts);

    return updateCategoriesPromise;
});

/**
 * Clear categories of the specified rows from the table
 * @param rowKeys of the rows to clear
 * @param tableView that contains the rows
 * @param actionSource the action source
 */
export const clearCategoriesFromTable = createLegacyActionWithDatapoint(
    'clearCategories',
    datapoints.clearCategories
)(function clearCategoriesFromTable(
    rowKeys: string[],
    tableView: TableView,
    actionSource: ActionSource
) {
    return updateRowCategoriesInternal(
        rowKeys,
        tableView,
        null /* categoriesToAdd */,
        null /* categoriesToRemove */,
        true /* clearCategories */,
        actionSource
    );
});

/**
 * Remove categories from the specified rows from the table
 * @param rowKeys of the rows to remove categories to
 * @param tableView that contains the rows
 * @param categoriesToRemove the categories to be removed to the rows
 * @param actionSource the action source
 */
export const removeCategoriesFromTable = createLegacyActionWithDatapoint(
    'removeCategoriesFromTable',
    datapoints.removeCategories
)(function removeCategoriesFromTable(
    rowKeys: string[],
    tableView: TableView,
    categoriesToRemove: string[],
    actionSource: ActionSource
) {
    return updateRowCategoriesInternal(
        rowKeys,
        tableView,
        null /* categoriesToAdd */,
        categoriesToRemove,
        false /* clearCategories */,
        actionSource
    );
});

/**
 * INternal function to do category operation
 * @param rowKeys of the rows to act upo
 * @param tableView that contains the rows
 * @param categoriesToAdd the categories to be added to the rows
 * @param categoriesToRemove the categories to be removed to the rows
 * @param clearCategories whether to clear categories or not
 * @param actionSource the action source
 * @param promiseToWaitUpon the promise to wait upon before sending the request for category update
 */
function updateRowCategoriesInternal(
    rowKeys: string[],
    tableView: TableView,
    categoriesToAdd: string[],
    categoriesToRemove: string[],
    clearCategories: boolean,
    actionSource: ActionSource,
    promiseToWaitUpon?: Promise<boolean>
): Promise<void> {
    if (tableView.tableQuery.listViewType == ReactListViewType.Conversation) {
        return updateConversationsCategoriesInternal(
            rowKeys,
            tableView,
            categoriesToAdd,
            categoriesToRemove,
            clearCategories,
            actionSource,
            promiseToWaitUpon
        );
    } else {
        return updateItemsCategoriesInternal(
            rowKeys,
            tableView,
            categoriesToAdd,
            categoriesToRemove,
            clearCategories,
            actionSource,
            promiseToWaitUpon
        );
    }
}
